import {
  Button,
  Icon,
  Table,
  addToast,
  showDialogConfirm,
  useMobile,
} from '@octano/global-ui';
import {
  CellFormatOptions,
  ColumnTable,
  PaginationType,
} from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FacultyListItem,
  FacultyListResponse,
  deleteFacultyRequest,
  getFacultiesRequest,
  getFacultyRelatedSchoolsRequest,
} from '../../../api/requests/faculties';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { TextEllipsis } from '../../../components/text';
import TableEmptyContent from '../../../components/text/TableEmptyContent';
import { useLayoutState } from '../../../hooks/useLayoutState';
import { useLoadingState } from '../../../hooks/useLoadingState';
import FacultyModal from './FacultyModal';

const ITEMS_PER_PAGE_DEFAULT = 10;
const PAGE_INITIAL_DEFAULT = 1;

const FacultyList = () => {
  const { showTabs } = useLayoutState();
  const isMobile = useMobile();
  const [showForm, setShowForm] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState<FacultyListItem | null>(
    null,
  );

  useEffect(() => {
    showTabs();
  }, [showTabs]);

  useEffect(() => {
    if (!showForm) {
      setRecordToEdit(null);
    }
  }, [showForm]);

  const { t } = useTranslation();

  const [facultiesData, setFacultiesData] = useState<FacultyListResponse>({
    data: [],
    total_pages: 0,
    total: 0,
  });

  const [currentPage, setCurrentPage] = useState(PAGE_INITIAL_DEFAULT);
  const [itemsPerPage] = useState(ITEMS_PER_PAGE_DEFAULT);
  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const getFacultiesList = useCallback(async () => {
    setShowForm(false);
    setRecordToEdit(null);
    const { error, data: response } = await getFacultiesRequest(
      itemsPerPage,
      currentPage - 1,
    );
    if (response) {
      setFacultiesData(response);
      setErrorLoading(undefined);
    } else if (error) {
      setErrorLoading(error.code);
      return;
    }
    setLoading(false);
  }, [itemsPerPage, currentPage, setErrorLoading, setLoading]);

  useEffect(() => {
    getFacultiesList();
  }, [getFacultiesList]);

  const handlerOnChangePage = useCallback(
    async (page: number) => setCurrentPage(page),
    [],
  );

  const handleDelete = useCallback(
    async (faculty: FacultyListItem) => {
      const { data: relatedSchools } = await getFacultyRelatedSchoolsRequest(
        faculty.id,
      );

      const schoolsList = relatedSchools?.length
        ? '\n\n' + relatedSchools.map((s) => `- ${s.name}`).join('\n')
        : '';

      const subtitle = relatedSchools?.length
        ? `${t(
            'maintainers.deleteFacultyConfirm.relatedSchoolsWarning',
          )}${schoolsList}`
        : t('maintainers.deleteFacultyConfirm.subtitle');

      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t('maintainers.deleteFacultyConfirm.title'),
        subtitle,
        btnConfirm: {
          text: t('common.actions.confirm'),
          onConfirm: async () => {
            const { error } = await deleteFacultyRequest(faculty.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t('maintainers.deleteFacultyConfirm.deleteFailed'),
              });
            } else {
              addToast({
                icon: 'success',
                color: 'success',
                text: t('maintainers.deleteFacultyConfirm.deleteSuccess'),
              });
              getFacultiesList();
            }
          },
        },
        btnClose: { text: t('common.actions.cancel') },
      });
    },
    [t, getFacultiesList],
  );

  const pagination = useMemo<PaginationType | undefined>(() => {
    if (facultiesData.total > itemsPerPage) {
      return {
        totalItems: facultiesData.total,
        itemsPerPage,
        totalPages: facultiesData.total_pages,
        currentPage,
        onChangePage: handlerOnChangePage,
      };
    }
  }, [facultiesData, itemsPerPage, currentPage, handlerOnChangePage]);

  const columns = useMemo<ColumnTable<FacultyListItem>[]>(() => {
    const actionColumns = [
      {
        columnName: 'id' as const,
        headerText: '',
        width: '0%',
        cellFormat: (options: CellFormatOptions) => (
          <span
            onClick={() => {
              setRecordToEdit(options.row);
              setShowForm(true);
            }}
            className="cursor-pointer"
          >
            <Icon name="edit" key="edit" color="secondary" />
          </span>
        ),
      },
      {
        columnName: 'id' as const,
        headerText: '',
        width: '0%',
        cellFormat: (options: CellFormatOptions) => (
          <span
            onClick={() => handleDelete(options.row)}
            className="cursor-pointer"
          >
            <Icon name="trash" key="trash" color="secondary" />
          </span>
        ),
      },
    ];

    return !isMobile
      ? [
          {
            columnName: 'code' as const,
            headerText: t('common.forms.code'),
            bold: true,
            width: '20%',
          },
          {
            columnName: 'name' as const,
            headerText: t('common.forms.name'),
            width: '25%',
          },
          {
            columnName: 'description' as const,
            headerText: t('common.forms.description'),
            cellFormat: (options: CellFormatOptions) => (
              <TextEllipsis key={options.index} textWidth={350}>
                {options.value}
              </TextEllipsis>
            ),
          },
          ...actionColumns,
        ]
      : [
          {
            columnName: 'name' as const,
            headerText: t('common.forms.name'),
            width: '100%',
            cellFormat: (options: CellFormatOptions) => (
              <div className="text-left">{options.value}</div>
            ),
          },
          ...actionColumns,
        ];
  }, [t, isMobile, handleDelete]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
        loadingAction={loading}
      />
    );
  }
  if (loading) {
    return <Loading insideCard />;
  }
  return (
    <div className="g-table-container">
      <Button
        text={t('maintainers.facultiesForm.addTitle')}
        type="button"
        size="sm"
        icon="plus"
        className="g-add-button mb-3 mt-3"
        onClick={() => setShowForm(true)}
      />
      <Table
        columns={columns}
        data={facultiesData.data}
        pagination={pagination}
        noResultsText={
          <TableEmptyContent
            title={t('maintainers.facultiesList.facultiesTableEmptyTitle')}
            subtitle={t(
              'maintainers.facultiesList.facultiesTableEmptySubtitle',
            )}
          />
        }
      />
      <FacultyModal
        show={showForm}
        reloadList={getFacultiesList}
        toggleModal={() => {
          setShowForm(!showForm);
          setRecordToEdit(null);
        }}
        recordToEdit={recordToEdit}
      />
    </div>
  );
};

export default FacultyList;
